// Day-boundary semantics for bookings — THE single source.
//
// This rule was previously inlined per call site and drifted twice (the
// `>=` vs `>` checkout boundary fixed on PR #99, and the backfill script
// carrying the pre-fix predicate). Every consumer — API routes and
// maintenance scripts — must import it from here, never re-type it.
//
// The rule: a stay occupies [check_in, check_out) — checkout day is
// EXCLUSIVE, matching the residence rule (spansDay in apps/web format.ts):
// a stay ending today is already over. The check_in leg keeps SAME-DAY
// bookings (check_in = check_out — real day-boarding stays) in scope: a
// stay starting today has not ended, whatever its checkout says. Cancelled
// bookings are frozen records and always excluded.

// ONE CLOCK. The kennel operates in Ireland, so the business day is the
// Europe/Dublin calendar date — never the DB session's UTC CURRENT_DATE and
// never the server host's clock. Between 00:00 and 01:00 Irish summer time
// UTC is still on YESTERDAY: a dog removal at 00:30 would mutate a different
// set of bookings than the UI shows. Every "today" that reaches SQL must be
// derived here and bound in as a parameter.

/** The kennel's business timezone. */
export const BUSINESS_TZ = "Europe/Dublin";

// The local calendar date (YYYY-MM-DD) in a timezone for a given instant. Pure
// (takes the instant in) so it unit-tests deterministically; callers pass
// `new Date()` at the edge. `en-CA` formats as YYYY-MM-DD; the timeZone
// handles DST. (Moved here from apps/api me/window.ts — Codex P2.)
export function localDateIso(at: Date, timeZone: string = BUSINESS_TZ): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone }).format(at);
}

/**
 * The business day: today's calendar date (YYYY-MM-DD) in Europe/Dublin.
 * Pass `now` in tests for determinism; production callers use the default.
 */
export function businessToday(now: Date = new Date()): string {
  return localDateIso(now, BUSINESS_TZ);
}

/**
 * The next business day after an ISO date (YYYY-MM-DD) — pure calendar math,
 * DST-proof: it steps the calendar date, not a 24-hour instant, so it never
 * drifts across a spring-forward/fall-back night. The staff Tomorrow briefing
 * derives its day from businessToday() through this.
 */
export function nextBusinessDayIso(iso: string): string {
  return businessDayPlus(iso, 1);
}

/**
 * An ISO date stepped by N calendar days (negative allowed) — the same
 * DST-proof calendar math as nextBusinessDayIso, generalised. THE single
 * derivation of the staff briefing's look-ahead days (today+1, today+2):
 * the /me and /staff/:id day routes, the client's staleness expected-date
 * functions, and the demo seed script all step days through this, so the
 * window cannot drift between server, client and tooling.
 */
export function businessDayPlus(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * SQL predicate for "this booking has not yet ended" — the set of bookings
 * a household-roster change (dog add/remove) applies to.
 *
 * @param alias  table alias to prefix columns with (e.g. "b"), or "" when
 *               the query addresses the bookings table unqualified.
 * @param today  SQL expression for the business day — a bound placeholder
 *               (e.g. "$1::date") whose value the caller supplies from
 *               businessToday(). REQUIRED, no CURRENT_DATE default:
 *               CURRENT_DATE is the DB session's UTC date, which is
 *               yesterday's business day around Irish-summer midnight.
 */
/**
 * The ONE definition of "this booking is a real stay", for every figure that
 * counts, sums or texts about one.
 *
 * `requested` joined the vocabulary with portal-foundations, and nine SQL sites
 * across the API were treating `status <> 'cancelled'` as this question: the
 * revenue series, daily earned, a household's lifetime visit count and its
 * first-booking flag, both residence predicates, the staff self-view briefing,
 * the automated staff daily brief — which TEXTS people — and the Bookings In-on
 * filter. A `requested` booking is a stay NOBODY HAS ACCEPTED, and counting one
 * as revenue, as an occupancy, or as a reason to text a carer about a dog that
 * is not coming is exactly the hazard this repo already documents: it is why
 * viewings got their own table rather than a `kind` column on bookings.
 *
 * A DENY-LIST, deliberately. An allow-list of live statuses would need editing
 * every time the vocabulary grows, and the failure mode of forgetting is a stay
 * that silently stops counting. This way a new status counts until somebody
 * decides otherwise — the safe direction to be wrong in.
 */
export function countsAsStaySql(alias: string): string {
  const p = alias ? `${alias}.` : "";
  // `declined` joined 2026-10-10 (accept-or-decline AC5): a request she said
  // no to holds no night, earns nothing, and texts no carer.
  return `${p}status NOT IN ('cancelled','requested','declined')`;
}

export function notYetEndedSql(alias: string, today: string): string {
  const p = alias ? `${alias}.` : "";
  return `(${p}check_out > ${today} OR ${p}check_in >= ${today}) AND ${countsAsStaySql(alias)}`;
}

/**
 * SQL predicate for "this booking has the dog in residence ON `day` and
 * CONTINUES past it" — spans the day (check_in <= day < check_out), a real
 * overnight stay (check_out > check_in drops zero-night day-boarders), cancelled
 * excluded. Single-sourced here like notYetEndedSql: the staff briefing's care
 * cards select residents through it, and its leaving query reuses it as the
 * continuation guard (a dog is leaving today only if it has no booking that is
 * resident-past-today), so the two can never disagree on what "still here"
 * means.
 *
 * @param alias  table alias to prefix columns with (e.g. "b"), or "" unqualified.
 * @param day    SQL expression for the business day — a bound placeholder
 *               (e.g. "$1::date"). REQUIRED, no CURRENT_DATE default (same
 *               Irish-summer-midnight reason as notYetEndedSql).
 */
export function residentPastDaySql(alias: string, day: string): string {
  const p = alias ? `${alias}.` : "";
  return `${p}check_in <= ${day} AND ${p}check_out > ${day} AND ${p}check_out > ${p}check_in AND ${countsAsStaySql(alias)}`;
}

// --- human day and range labels --------------------------------------------
//
// MOVED HERE from apps/web (portal-foundations), for the reason the pricing
// helper moved: two sides were formatting the same range and only one of them
// could reach the formatter.
//
// The symptom, seen in the browser rather than in a test: the capacity screen
// listed an override as "12 Dec 2027 to 19 Dec 2027" and the overlap refusal —
// a message composed in the Worker, which had no formatter — toasted
// "2027-12-12 to 2027-12-19" two inches above it. Both correct, neither the
// same, and the reader has to work out that they are one range.
//
// Deliberately NOT the desk's stayDatesLabel, which collapses a range into
// "10–14 Jul" for a stay. A capacity block is a rule rather than a stay, and a
// rule reads better spelled out, year included — it is usually about next
// Christmas.
const MONTHS_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** `2027-12-19` → `19 Dec 2027`. A malformed value is returned as it came. */
export function dayLabel(iso: string): string {
  const [y, m, d] = iso.split("-");
  const month = MONTHS_ABBR[Number(m) - 1];
  if (!y || !month || !d) return iso;
  return `${Number(d)} ${month} ${y}`;
}

/**
 * Both ends INCLUSIVE — "12 Dec 2027 to 19 Dec 2027" closes the 19th. A single
 * day says so once rather than twice.
 */
export function rangeLabel(range: { startsOn: string; endsOn: string }): string {
  if (range.startsOn === range.endsOn) return dayLabel(range.startsOn);
  return `${dayLabel(range.startsOn)} to ${dayLabel(range.endsOn)}`;
}

const WEEKDAYS_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * A restricted drop-off and collection day as the card lists it, and as the
 * refusal that points at it names it (handover-days): `Fri 23 Oct`,
 * `Thu 24 – Fri 25 Dec`, `Thu 31 Dec – Fri 1 Jan`. The weekday is in it because
 * Patricia thinks of these days by weekday — the bank holiday Monday, the
 * mid-term Friday. No year: the card lists only today onwards, and the
 * prototype Freddi signed off reads this way.
 */
export function handoverDayLabel(range: { startsOn: string; endsOn: string }): string {
  const parts = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    const wd = WEEKDAYS_ABBR[new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay()]!;
    return { wd, d: d!, mon: MONTHS_ABBR[m! - 1]!, key: iso.slice(0, 7) };
  };
  const a = parts(range.startsOn);
  if (range.startsOn === range.endsOn) return `${a.wd} ${a.d} ${a.mon}`;
  const b = parts(range.endsOn);
  return a.key === b.key ? `${a.wd} ${a.d} – ${b.wd} ${b.d} ${b.mon}` : `${a.wd} ${a.d} ${a.mon} – ${b.wd} ${b.d} ${b.mon}`;
}
